import React, { useState, useEffect } from "react";
import {
  CheckCircle,
  Shield,
  AlertTriangle,
  Users,
  Mail,
  MapPin,
  ChevronRight,
  ScrollText,
  Clock,
  Briefcase,
} from "lucide-react";

const TermsConditionsPage = () => {
  const [activeSection, setActiveSection] = useState(1);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sections = [
    {
      id: 1,
      title: "Acceptance of Terms",
      icon: CheckCircle,
      color: "blue",
      content:
        "By registering on, accessing, browsing, downloading or using the Bumppy Platform for any general purpose or for the specific purpose of availing any Bumppy Service, You agree to be bound by the single-sign-on ID (SSOID) terms and conditions set forth below as well as by the service-specific terms and conditions applicable to each Bumppy Service.",
      points: [
        "By using the Bumppy Platform, you automatically agree to all Terms & Conditions",
        "If you do not accept the T&Cs, you must immediately terminate your use of the platform",
        "Accepting the T&Cs constitutes a legal contract between you and Bumppy Payments",
        "You must be at least 18 years of age to use our services",
        "All services are rendered through the Bumppy Platform under the brand name 'Bumppy Payments'",
      ],
    },
    {
      id: 2,
      title: "User Eligibility",
      icon: Users,
      color: "purple",
      points: [
        "The Bumppy Services are not available to persons under the age of 18",
        "Services are not available to anyone previously suspended or removed by Bumppy",
        "You represent that you are at least 18 years of age and have the right to enter this agreement",
        "You shall not impersonate any person or entity or misrepresent your identity",
        "Bumppy reserves the right to suspend accounts for any violation of T&Cs",
      ],
    },
    {
      id: 3,
      title: "Limitation of Liability",
      icon: Shield,
      color: "red",
      subsections: [
        {
          subtitle: "No Liability for Damages",
          items: [
            "Bumppy will not be liable for any indirect, incidental, consequential or punitive damages",
            "This includes lost business opportunities, revenues, or loss of anticipated profits",
            "Applies to damages arising from the use or inability to use the platform",
          ],
        },
        {
          subtitle: "Liability Cap",
          items: [
            "Maximum liability is limited to the amount paid by you, OR Rs. 5,000, whichever is less",
            "Bumppy has set its prices in reliance upon these specific limitations",
          ],
        },
      ],
    },
    {
      id: 4,
      title: "Disclaimer & Warranties",
      icon: AlertTriangle,
      color: "amber",
      subsections: [
        {
          subtitle: "General Disclaimers",
          items: [
            "Services are provided on an 'AS IS' and 'AS AVAILABLE' basis",
            "Bumppy disclaims all warranties – statutory, express or implied",
            "Use of Bumppy Services is at your sole risk",
          ],
        },
        {
          subtitle: "Platform Reliability",
          items: [
            "No warranty that services will be uninterrupted or error-free",
            "You are solely responsible for any damage to your property or loss of data",
          ],
        },
      ],
    },
    {
      id: 5,
      title: "Communication & Notices",
      icon: ScrollText,
      color: "cyan",
      points: [
        "Bumppy may provide notices by e-mail, SMS, push notifications, or platform postings",
        "Official notices to Bumppy must be sent via registered mail to support@bumppy.com",
        "Address: B-218, Sector-62, Noida, 201301, Uttar Pradesh, India",
      ],
    },
    {
      id: 6,
      title: "Services Offered ",
      icon: Briefcase,
      color: "emerald",
      subsections: [
        {
          subtitle: "BBPS Services",
          items: [
            "Bumppy Payments provides Bharat Bill Payment System (BBPS) services through authorized banking and payment partners in accordance with the guidelines issued by the National Payments Corporation of India (NPCI) and the Reserve Bank of India (RBI).",
            "The BBPS platform enables users to make secure, convenient, and interoperable payments for various categories of bills, including electricity, water, gas, broadband, landline, mobile postpaid, DTH, FASTag, insurance premiums, loan repayments, municipal taxes, education fees, and other billers available on the BBPS network.",
            "Bill payment services are available only for billers that are onboarded to the BBPS ecosystem. The availability of specific billers and services may vary depending on regulatory requirements, banking partner support, and technical feasibility.",
            "Users are responsible for verifying the bill details, consumer number, account information, bill amount, and other payment details before confirming a transaction. Bumppy Payments shall not be responsible for losses arising from incorrect information entered by the user.",
            "Upon successful processing of a payment, a transaction reference number and payment confirmation will be generated. Users are advised to retain the transaction receipt for future reference and dispute resolution, if required.",
            "Bumppy Payments reserves the right to modify, suspend, or discontinue any BBPS service, category, or biller without prior notice due to regulatory changes, operational requirements, technical issues, or instructions received from NPCI, partner banks, or regulatory authorities.",
          ],
        },
      ],
    },
    {
      id: 7,
      title: "Governing Law & Jurisdiction",
      icon: Briefcase,
      color: "emerald",
      subsections: [
        {
          subtitle: "Governing Law & Jurisdiction",
          items: [
            "These Terms & Conditions shall be governed by and construed in accordance with the laws of the Republic of India.",
            "Any dispute, controversy, claim, or difference arising out of or relating to these Terms & Conditions, the use of the Website, or any services provided by Bumppy Payments, including any question regarding their existence, validity, interpretation, performance, or termination, shall be subject to the exclusive jurisdiction of the competent courts located in Noida, Uttar Pradesh, India, unless otherwise required by applicable law.",
            "Before initiating any legal proceedings, the parties shall make reasonable efforts to resolve disputes amicably through mutual discussions and negotiations.",
            "If any provision of these Terms & Conditions is held to be invalid, illegal, or unenforceable by a court of competent jurisdiction, the remaining provisions shall continue to remain valid and enforceable to the fullest extent permitted under applicable law.",
            "By accessing or using the Bumppy Payments website or services, you acknowledge that you have read, understood, and agreed to be bound by these Terms & Conditions and submit to the jurisdiction of the courts specified above.",
          ],
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-inter">
      <div className="relative bg-[#0F172A] py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-blue-600/10 to-transparent"></div>

        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
            <Clock size={16} />
            <span>Last Updated: October 2023</span>
          </div>
          <h1 className="text-4xl font-spacegrotesk md:text-6xl font-bold text-white mb-6 tracking-tight">
            Terms <span className="text-blue-500">&</span> Conditions
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Legal guidelines and user agreements for the Bumppy Payments
            ecosystem. Please read these carefully to understand your rights.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 lg:py-20">
        <div className="flex flex-col lg:flex-row gap-12">
          <aside className="hidden lg:block w-72 h-fit sticky top-10">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 px-4">
                Navigation
              </p>
              <nav className="space-y-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      activeSection === s.id
                        ? "bg-blue-50 text-blue-600 shadow-sm"
                        : "text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    <s.icon size={18} />
                    <span className="font-semibold text-sm truncate">
                      {s.title}
                    </span>
                    {activeSection === s.id && (
                      <ChevronRight size={14} className="ml-auto" />
                    )}
                  </button>
                ))}
              </nav>
            </div>

            <div className="mt-6 p-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl text-white shadow-lg shadow-blue-100">
              <h4 className="font-bold mb-2">Need Help?</h4>
              <p className="text-xs text-blue-100 mb-4 leading-relaxed">
                If you have any questions regarding these terms, contact our
                legal team.
              </p>
              <a
                href="mailto:support@bumppy.com"
                className="text-xs font-bold bg-white/20 hover:bg-white/30 px-3 py-2 rounded-lg transition-colors block text-center uppercase tracking-wider"
              >
                Contact Support
              </a>
            </div>
          </aside>

          <main className="flex-1 space-y-8">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex gap-4 items-start">
              <div className="bg-amber-100 p-2 rounded-lg">
                <AlertTriangle className="text-amber-600" size={24} />
              </div>
              <div>
                <h3 className="text-amber-900 font-bold mb-1">
                  Important Notice
                </h3>
                <p className="text-amber-800/80 text-sm leading-relaxed">
                  By using Bumppy services, you automatically accept these Terms
                  & Conditions. If you do not agree, you must immediately stop
                  using our platform.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {sections.map((section) => (
                <div
                  key={section.id}
                  className={`bg-white rounded-3xl border transition-all duration-300 ${
                    activeSection === section.id
                      ? "border-blue-200 shadow-xl shadow-blue-50/50"
                      : "border-slate-200 shadow-sm"
                  }`}
                >
                  <button
                    onClick={() => setActiveSection(section.id)}
                    className="w-full text-left p-6 md:p-8 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-5">
                      <div
                        className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-${section.color}-50 text-${section.color}-600`}
                      >
                        <section.icon size={24} />
                      </div>
                      <h2 className="text-xl md:text-2xl font-bold text-slate-800">
                        {section.id}. {section.title}
                      </h2>
                    </div>
                    <div
                      className={`transition-transform duration-300 ${activeSection === section.id ? "rotate-90" : ""}`}
                    >
                      <ChevronRight className="text-slate-400" />
                    </div>
                  </button>

                  {activeSection === section.id && (
                    <div className="px-6 pb-8 md:px-8 md:pb-10 animate-fadeIn">
                      <div className="h-px bg-slate-100 mb-8" />

                      {section.content && (
                        <p className="text-slate-600 leading-relaxed mb-8 text-lg font-medium italic">
                          "{section.content}"
                        </p>
                      )}

                      {section.points && (
                        <div className="grid gap-4">
                          {section.points.map((pt, i) => (
                            <div
                              key={i}
                              className="flex gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100 group hover:bg-white hover:border-blue-200 transition-all"
                            >
                              <CheckCircle
                                className="text-blue-500 mt-1 flex-shrink-0"
                                size={18}
                              />
                              <span className="text-slate-700 font-medium">
                                {pt}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {section.subsections && (
                        <div className="space-y-8 mt-4">
                          {section.subsections.map((sub, i) => (
                            <div key={i}>
                              <h4 className="flex items-center gap-2 text-slate-900 font-bold mb-4">
                                <span className="w-1.5 h-6 bg-blue-500 rounded-full"></span>
                                {sub.subtitle}
                              </h4>
                              <ul className="space-y-3 ml-4">
                                {sub.items.map((item, idx) => (
                                  <li
                                    key={idx}
                                    className="flex items-start gap-3 text-slate-600"
                                  >
                                    <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300" />
                                    <span className="text-sm">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-12">
              <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm group hover:border-blue-500 transition-all">
                <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 mb-6 group-hover:bg-blue-600 group-hover:text-white transition-all">
                  <Mail />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Legal Queries
                </h3>
                <p className="text-slate-500 mb-4 text-sm">
                  Our legal team is available for any clarifications.
                </p>
                <a
                  href="mailto:support@bumppy.com"
                  className="text-blue-600 font-bold"
                >
                  support@bumppy.com
                </a>
              </div>

              <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm group hover:border-indigo-500 transition-all">
                <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                  <MapPin />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Registered Office
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  BUMPPY PAYMENTS PVT LTD
                  <br />
                  B-218, Ithum Tower, Sector-62
                  <br />
                  Noida, 201301, Uttar Pradesh
                </p>
              </div>
            </div>
          </main>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        
        .bg-blue-50 { background-color: #eff6ff; }
        .text-blue-600 { color: #2563eb; }
        .bg-purple-50 { background-color: #faf5ff; }
        .text-purple-600 { color: #9333ea; }
        .bg-red-50 { background-color: #fef2f2; }
        .text-red-600 { color: #dc2626; }
        .bg-amber-50 { background-color: #fffbeb; }
        .text-amber-600 { color: #d97706; }
        .bg-cyan-50 { background-color: #ecfeff; }
        .text-cyan-600 { color: #0891b2; }
      `}</style>
    </div>
  );
};

export default TermsConditionsPage;
